import {useRef, useState} from 'react';
import {useNavigateToLibraryRecord} from '@/matic/features/_shared/hooks/useNavigateToLibraryRecord';
import {
  Download,
  FileText,
  Mail,
  MessageSquare,
  Plus,
  Tag,
  Trash2,
} from 'lucide-react';
import {Button} from '@/matic/components/ui/button';
import {RhsPanelHeader} from '@/matic/features/_shared/components/layout/RhsPanelHeader';
import {useClickOutside} from '@/matic/lib/hooks/useClickOutside';
import {formatBytes} from '@/matic/lib/utils/format-bytes';
import {
  AssociationPicker,
  useInlineCreateAssociation,
  type AssociationPickerSelection,
} from '@/matic/features/relay/components/shared/AssociationPicker';
import {AssociationChip} from '@/matic/features/relay/components/shared/AssociationChip';
import {useAttachmentSourceForFile} from '@/matic/features/relay/hooks/useAttachmentSourceForFile';
import type {FileCategory, ManagedFile} from '../types/files-types';
import {TagPicker} from './TagPicker';
import {FileDocumentViewer} from './FileDocumentViewer';

type UpdateFileInput = {name?: string; categoryId?: string | null};

// ---------------------------------------------------------------------------
// Inline association strip
// ---------------------------------------------------------------------------

type AssociationStripProps = {
  file: ManagedFile;
  canManage: boolean;
  isBusy: boolean;
  onAdd?: (objectTypeId: string, objectRecordId: string | null) => void;
  onRemove?: (assocId: string) => void;
  onNavigateToAssociation?: (typeId: string, recordId: string | null) => void;
};

function AssociationStrip({
  file,
  canManage,
  isBusy,
  onAdd,
  onRemove,
  onNavigateToAssociation,
}: AssociationStripProps) {
  const defaultNavigate = useNavigateToLibraryRecord();
  const navigateToRecord = onNavigateToAssociation ?? defaultNavigate;
  const [showPicker, setShowPicker] = useState(false);

  // Quick-create: mint a new record and link it to this file in one step.
  const inlineCreate = useInlineCreateAssociation((sel) => {
    onAdd?.(sel.objectTypeId, sel.objectRecordId);
    setShowPicker(false);
  });

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {file.associations.map((assoc) => (
        <AssociationChip
          key={assoc.id}
          assoc={assoc}
          includeTypeName
          onNavigate={navigateToRecord}
          onRemove={canManage && !isBusy ? onRemove : undefined}
        />
      ))}
      {canManage &&
        onAdd &&
        (showPicker ? (
          <AssociationPicker
            allowCategorySelection
            autoOpen
            className="min-w-[180px]"
            onAdd={(sel: AssociationPickerSelection) => {
              onAdd(sel.objectTypeId, sel.objectRecordId);
              setShowPicker(false);
            }}
            onClose={() => {
              setShowPicker(false);
            }}
            inlineCreate={inlineCreate}
          />
        ) : (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-neutral-400 transition-colors hover:text-neutral-600"
            onClick={() => {
              setShowPicker(true);
            }}
          >
            <Plus className="h-3 w-3" />
            Add association…
          </button>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category chip — click-to-edit (or static when read-only)
// ---------------------------------------------------------------------------

const chipClass =
  'inline-flex max-w-full items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600';

type CategoryChipProps = {
  categoryId: string | null;
  categoryLabel: string | null;
  categories: FileCategory[];
  onRecategorize?: (categoryId: string | null) => void;
  onCreateCategory?: (name: string, sortOrder: number) => Promise<FileCategory>;
  isCreateCategoryPending?: boolean;
};

function CategoryChip({
  categoryId,
  categoryLabel,
  categories,
  onRecategorize,
  onCreateCategory,
  isCreateCategoryPending,
}: CategoryChipProps) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(
    ref,
    () => {
      setEditing(false);
    },
    editing
  );

  // Read-only surfaces show the category only when one is set.
  if (!onRecategorize) {
    if (!categoryLabel) return null;
    return (
      <span className={chipClass}>
        <Tag className="h-3 w-3 shrink-0" />
        <span className="truncate">{categoryLabel}</span>
      </span>
    );
  }

  if (editing) {
    return (
      <div ref={ref} className="min-w-[200px]">
        <TagPicker
          value={categoryId}
          categories={categories}
          onChange={(id) => {
            onRecategorize(id);
            setEditing(false);
          }}
          onCreateCategory={onCreateCategory}
          isCreatePending={isCreateCategoryPending}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setEditing(true);
      }}
      className={`${chipClass} transition-colors hover:bg-neutral-200 hover:text-neutral-900 ${
        categoryLabel ? '' : 'text-neutral-400'
      }`}
    >
      <Tag className="h-3 w-3 shrink-0" />
      <span className="truncate">{categoryLabel ?? 'Add tag'}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Source thread link — only for relay-sourced files
// ---------------------------------------------------------------------------

type SourceLinkProps = {
  fileId: string;
  onNavigate: (threadId: string) => void;
};

function SourceLink({fileId, onNavigate}: SourceLinkProps) {
  const {data: source} = useAttachmentSourceForFile(fileId, true);
  if (!source) return null;
  const label = source.emailSubject ?? source.emailSender;
  return (
    <button
      type="button"
      onClick={() => {
        onNavigate(source.threadId);
      }}
      className={`${chipClass} transition-colors hover:bg-neutral-200 hover:text-neutral-900`}
    >
      <Mail className="h-3 w-3 shrink-0" />
      <span className="truncate">From: {label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// File header — title, metadata, action buttons
// ---------------------------------------------------------------------------

/**
 * Build the meta subtitle (`filename · size`). The original filename is shown
 * next to the size only when it differs from the display name; category is no
 * longer included here — it lives in the click-to-edit category chip below.
 */
function buildFileMeta(file: ManagedFile): string {
  const parts: string[] = [];
  if (file.originalFilename !== file.name) parts.push(file.originalFilename);
  parts.push(formatBytes(file.sizeBytes));
  return parts.join(' · ');
}

type FileActionsProps = {
  file: ManagedFile;
  canManage: boolean;
  onDownload: (file: ManagedFile) => void;
  onDelete?: (file: ManagedFile) => void;
  onChatWithFile?: (file: ManagedFile) => void;
};

const iconButtonClass =
  'rounded p-1.5 text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600';

/**
 * Chat (labeled, dark — the app's default-CTA style, so it isn't missed next
 * to the icon-only actions) + download + (optional) delete. Rename and
 * recategorize are inline (the header title + the category chip), so there's
 * no overflow menu.
 */
function FileActions({
  file,
  canManage,
  onDownload,
  onDelete,
  onChatWithFile,
}: FileActionsProps) {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {onChatWithFile && (
        <Button
          type="button"
          variant="dark"
          size="sm"
          className="gap-1.5"
          onClick={() => {
            onChatWithFile(file);
          }}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Chat
        </Button>
      )}
      <button
        type="button"
        aria-label="Download"
        title="Download"
        onClick={() => {
          onDownload(file);
        }}
        className={iconButtonClass}
      >
        <Download className="h-4 w-4" />
      </button>
      {canManage && onDelete && (
        <button
          type="button"
          aria-label="Delete"
          title="Delete"
          onClick={() => {
            onDelete(file);
          }}
          className="rounded p-1.5 text-neutral-400 transition-colors hover:bg-error-50 hover:text-error-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Shell-headed (`hideTitleHeader`) row — the parent RHS shell owns the identity
 * row, so this keeps only the meta subtitle + file actions.
 */
function FilePreviewEmbeddedHeader({
  file,
  canManage,
  onDownload,
  onDelete,
  onChatWithFile,
}: {
  file: ManagedFile;
  canManage: boolean;
  onDownload: (file: ManagedFile) => void;
  onDelete?: (file: ManagedFile) => void;
  onChatWithFile?: (file: ManagedFile) => void;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-2">
      <p className="min-w-0 flex-1 truncate text-xs text-neutral-500">
        {buildFileMeta(file)}
      </p>
      <FileActions
        file={file}
        canManage={canManage}
        onDownload={onDownload}
        onDelete={onDelete}
        onChatWithFile={onChatWithFile}
      />
    </div>
  );
}

type FilePreviewMetadataStripProps = {
  file: ManagedFile;
  canManage: boolean;
  isAssociationBusy: boolean;
  categories: FileCategory[];
  categoryLabel: string | null;
  onAddAssociation?: (
    objectTypeId: string,
    objectRecordId: string | null
  ) => void;
  onRemoveAssociation?: (assocId: string) => void;
  onNavigateToAssociation?: (typeId: string, recordId: string | null) => void;
  onNavigateToThread?: (threadId: string) => void;
  onRecategorize?: (categoryId: string | null) => void;
  onCreateCategory?: (name: string, sortOrder: number) => Promise<FileCategory>;
  isCreateCategoryPending?: boolean;
};

function showSourceLinkFor(props: FilePreviewMetadataStripProps): boolean {
  return (
    props.file.sourceKind === 'relay_email_attachment' &&
    Boolean(props.onNavigateToThread)
  );
}

function hasMetadataStripContent(
  props: FilePreviewMetadataStripProps
): boolean {
  return (
    props.canManage ||
    props.file.associations.length > 0 ||
    showSourceLinkFor(props) ||
    Boolean(props.categoryLabel)
  );
}

/** Association chips + category chip + caller-gated source link — no wrapper. */
function FilePreviewMetadataStripContent(props: FilePreviewMetadataStripProps) {
  const {
    file,
    canManage,
    isAssociationBusy,
    categories,
    categoryLabel,
    onAddAssociation,
    onRemoveAssociation,
    onNavigateToAssociation,
    onRecategorize,
    onCreateCategory,
    isCreateCategoryPending,
  } = props;

  const showSourceLink = showSourceLinkFor(props);
  const showCategory = Boolean(categoryLabel) || Boolean(onRecategorize);
  const showAssoc = canManage || file.associations.length > 0;

  return (
    <div className="space-y-1.5">
      {showAssoc && (
        <AssociationStrip
          file={file}
          canManage={canManage}
          isBusy={isAssociationBusy}
          onAdd={onAddAssociation}
          onRemove={onRemoveAssociation}
          onNavigateToAssociation={onNavigateToAssociation}
        />
      )}
      {(showCategory || showSourceLink) && (
        <div className="flex flex-wrap items-center gap-2">
          {showCategory && (
            <CategoryChip
              categoryId={file.categoryId}
              categoryLabel={categoryLabel}
              categories={categories}
              onRecategorize={onRecategorize}
              onCreateCategory={onCreateCategory}
              isCreateCategoryPending={isCreateCategoryPending}
            />
          )}
          {showSourceLink && (
            <SourceLink
              fileId={file.id}
              onNavigate={props.onNavigateToThread!}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Shell-headed (`hideTitleHeader`) strip — keeps the bordered wrapper. */
function FilePreviewMetadataStrip(props: FilePreviewMetadataStripProps) {
  if (!hasMetadataStripContent(props)) return null;

  return (
    <div className="shrink-0 border-b border-neutral-100 px-4 py-2">
      <FilePreviewMetadataStripContent {...props} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

type FilePreviewPanelProps = {
  file: ManagedFile | null;
  onClose: () => void;
  onDownload: (file: ManagedFile) => void;
  /** Parent RHS shells can own the identity row while this panel keeps actions/context. */
  hideTitleHeader?: boolean;
  /** When true, shows delete + inline rename/recategorize + the association strip */
  canManage?: boolean;
  onDelete?: (file: ManagedFile) => void;
  /**
   * Inline name + category edits. Present => the header title is click-to-edit
   * and the category chip is click-to-pick. Wire to the file update mutation.
   */
  onUpdateFile?: (file: ManagedFile, input: UpdateFileInput) => void;
  /** Inline category creation from the category chip's picker. */
  onCreateCategory?: (name: string, sortOrder: number) => Promise<FileCategory>;
  isCreateCategoryPending?: boolean;
  /** Called when user clicks a file-association chip to navigate to that record */
  onAddAssociation?: (
    objectTypeId: string,
    objectRecordId: string | null
  ) => void;
  onRemoveAssociation?: (assocId: string) => void;
  /**
   * Overrides the default navigate-to-Library behavior of an association chip.
   * Library callers use this to *append* the chip to the current scope
   * (narrow by intersection) instead of leaving the page.
   */
  onNavigateToAssociation?: (typeId: string, recordId: string | null) => void;
  categories?: FileCategory[];
  isAssociationBusy?: boolean;
  /**
   * When provided, relay-sourced files show a "From: <email>" badge.
   * Called with the source threadId — the parent decides where to navigate.
   */
  onNavigateToThread?: (threadId: string) => void;
  /**
   * When provided, the header actions gain a "Chat with this file" button.
   * Library callers pin the file to the Otto session and switch to the Chat
   * tab; the preview stays open so chat and file sit side by side.
   */
  onChatWithFile?: (file: ManagedFile) => void;
  /** Page to open/scroll the viewer to (1-indexed; PDF scroll mode only).
   *  Citation clicks pass the cited page here. */
  initialPage?: number;
  /** Incrementing key that re-triggers the scroll even to the same page —
   *  Scout's citation-click idiom. */
  scrollKey?: number;
};

export function FilePreviewPanel({
  file,
  onClose,
  onDownload,
  hideTitleHeader = false,
  canManage = false,
  onDelete,
  onUpdateFile,
  onCreateCategory,
  isCreateCategoryPending,
  onAddAssociation,
  onRemoveAssociation,
  onNavigateToAssociation,
  categories = [],
  isAssociationBusy = false,
  onNavigateToThread,
  onChatWithFile,
  initialPage,
  scrollKey,
}: FilePreviewPanelProps) {
  if (!file) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-neutral-50 p-6 text-center">
        <FileText className="h-10 w-10 text-neutral-300" />
        <h3 className="mt-3 text-sm font-semibold text-neutral-900">
          Select a file
        </h3>
        <p className="mt-1 max-w-xs text-sm text-neutral-500">
          Preview files here without leaving this record.
        </p>
      </div>
    );
  }

  const categoryLabel =
    file.categoryId && categories.length > 0
      ? (categories.find((c) => c.id === file.categoryId)?.name ?? null)
      : null;

  const canEditMeta = canManage && Boolean(onUpdateFile);

  const stripProps: FilePreviewMetadataStripProps = {
    file,
    canManage,
    isAssociationBusy,
    categories,
    categoryLabel,
    onAddAssociation,
    onRemoveAssociation,
    onNavigateToAssociation,
    onNavigateToThread,
    onRecategorize: canEditMeta
      ? (categoryId) => {
          onUpdateFile!(file, {categoryId});
        }
      : undefined,
    onCreateCategory,
    isCreateCategoryPending,
  };

  return (
    <div className="flex h-full flex-col bg-white">
      {hideTitleHeader ? (
        <>
          <FilePreviewEmbeddedHeader
            file={file}
            canManage={canManage}
            onDownload={onDownload}
            onDelete={onDelete}
            onChatWithFile={onChatWithFile}
          />
          <FilePreviewMetadataStrip {...stripProps} />
        </>
      ) : (
        <RhsPanelHeader
          title={file.name}
          titleTitleAttr={file.name}
          onRename={
            canEditMeta
              ? (next) => {
                  onUpdateFile!(file, {name: next});
                }
              : undefined
          }
          onClose={onClose}
          actions={
            <FileActions
              file={file}
              canManage={canManage}
              onDownload={onDownload}
              onDelete={onDelete}
              onChatWithFile={onChatWithFile}
            />
          }
          secondRow={
            <div className="space-y-1.5">
              <p className="truncate text-xs text-neutral-500">
                {buildFileMeta(file)}
              </p>
              {hasMetadataStripContent(stripProps) && (
                <FilePreviewMetadataStripContent {...stripProps} />
              )}
            </div>
          }
        />
      )}

      <div className="min-h-0 flex-1 overflow-hidden">
        <FileDocumentViewer
          file={file}
          className="h-full"
          initialPage={initialPage}
          scrollKey={scrollKey}
        />
      </div>
    </div>
  );
}
